import { App } from 'antd';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { clientConfig } from 'src/api/axios/config';
import {
  FanguardApiContractsUserUserDetailModel,
  FanguardApiContractsUserUserPreferencesRequestModel,
  FanguardApiContractsUserUserPreferencesResponseModel,
} from 'src/api/generated';

import { usersQueries, USERS_URI } from '@/api/queryKeys';
import { patch, post, put, remove } from '@/api/api';
import { Schema as UserSchema } from '@/pages/Users/UsersNew/config';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { useMutationWithMessages } from './helpers';
import { configQueries } from '@/api/queryKeys/config';
import { useAuth } from '@/contexts/AuthContext';

const useGetCurrentUser = () => {
  const { username } = useAuth();

  return useQuery({
    ...usersQueries.current(username ?? ''),
    placeholderData: keepPreviousData,
    enabled: !!username
  });
};

const useGetUser = (id: string | undefined) => {
  return useQuery({
    ...usersQueries.detail(id ?? ''),
    enabled: !!id,
  });
};

interface useGetUsersParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
}

const useGetUsers = ({ page = 1, pageSize = 25, searchTerm = '' }: useGetUsersParams) => {
  return useQuery(usersQueries.list({ page, pageSize, searchTerm }));
};

// redirect to user?
const useCreateUser = () => {
  return useMutationWithMessages<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, UserSchema>(
    (data) => post({ uri: USERS_URI, data }),
    {
      action: 'create',
      entityType: 'user',
      queryKeysToInvalidate: [usersQueries.list._def],
      customErrorMessage: true,
    },
  );
};

const useUpdateUser = (username: string | undefined | null) => {
  return useMutationWithMessages<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, UserSchema>(
    (data) => patch({ uri: `${USERS_URI}/${username}`, data }),
    {
      action: 'update',
      entityType: 'user',
      queryKeysToInvalidate: [usersQueries.list._def, usersQueries.detail(username ?? '').queryKey],
    },
  );
};

const useDeleteUser = () => {
  return useMutationWithMessages<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, string>(
    (id: string) => remove({ uri: `${USERS_URI}/${id}` }),
    {
      action: 'delete',
      entityType: 'user',
      queryKeysToInvalidate: [usersQueries.list._def],
    },
  );
};

const useResetUserPassword = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const data = {
    client: clientConfig.getClient()?.length > 0 ? clientConfig.getClient() : 'system',
  };

  return useMutation<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, string>({
    mutationFn: async (id: string) =>
      post({ uri: `${USERS_URI}/${id}/invitations/resetpassword`, data, headers: { client: data.client } }),
    onSuccess: () => {
      message.success(`${t('users.table.resetPasswordSuccess')}`);
    },
    onError: () => {
      message.error(`${t('users.table.resetPasswordError')}`);
    },
  });
};

const useUnlockUser = (options?: { invalidateAll?: boolean }) => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, string>({
    mutationFn: async (username: string) => post({ uri: `${USERS_URI}/${username}/unlock` }),
    onSuccess: (_, username) => {
      message.success(`${t('users.unlockUserSuccess')}`);
      if (options?.invalidateAll) {
        queryClient.invalidateQueries({ queryKey: usersQueries.list._def });
      } else {
        queryClient.invalidateQueries({ queryKey: usersQueries.detail(username).queryKey });
      }
    },
    onError: () => {
      message.error(`${t('users.unlockUserError')}`);
    },
  });
};

const useSetUserPassword = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  return useMutation<
    AxiosResponse<FanguardApiContractsUserUserDetailModel>,
    Error,
    { username: string; password: string }
  >({
    mutationFn: async ({ username, password }: { username: string; password: string }) =>
      put({
        uri: `${USERS_URI}/${username}/set-password`,
        data: {
          password,
          client: clientConfig.getClient()?.length > 0 ? clientConfig.getClient() : 'system',
        },
      }),
    onSuccess: () => {
      message.success(`${t('login.changeUserPasswordSuccess')}`);
    },
    onError: () => {
      message.error(`${t('login.changeUserPasswordError')}`);
    },
  });
};

const useSetCurrentUserPreferences = () => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsUserUserPreferencesResponseModel>,
    Error,
    FanguardApiContractsUserUserPreferencesRequestModel
  >((data) => put({ uri: `${USERS_URI}/preferences`, data }), {
    action: 'update',
    entityType: 'user',
    queryKeysToInvalidate: [configQueries.config._def],
  });
};

export {
  useCreateUser,
  useDeleteUser,
  useGetCurrentUser,
  useGetUser,
  useGetUsers,
  useResetUserPassword,
  useSetUserPassword,
  useUnlockUser,
  useUpdateUser,
  useSetCurrentUserPreferences,
};
